import React, { useState, useEffect } from "react";
import { 
  HiPencil, HiTrash, HiCheckCircle, HiPlus, HiSearch, 
  HiOutlineUserGroup, HiOutlineChartBar, HiOutlineShieldCheck, 
  HiOutlinePencilAlt, HiOutlineTrash, HiOutlinePlusCircle, 
  HiOutlineSupport, HiOutlineUserCircle, HiOutlineCode 
} from "react-icons/hi";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import * as bootstrap from "bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { PulseLoader } from "react-spinners";

const accessOptions = [
  { value: "Edit", label: "Edit", icon: <HiOutlinePencilAlt className="me-2" /> },
  { value: "Delete", label: "Delete", icon: <HiOutlineTrash className="me-2" /> },
  { value: "Add", label: "Add", icon: <HiOutlinePlusCircle className="me-2" /> }
];

const roleOptions = [
  { value: "Customer Care", label: "Customer Care", color: "#0d6efd", icon: <HiOutlineSupport className="me-2" /> },
  { value: "Admin", label: "Admin", color: "#dc3545", icon: <HiOutlineShieldCheck className="me-2" /> },
  { value: "Manager", label: "Manager", color: "#fd7e14", icon: <HiOutlineUserCircle className="me-2" /> },
  { value: "Developer", label: "Developer", color: "#20c997", icon: <HiOutlineCode className="me-2" /> }
];

export default function UserTable() {
  const [selectedAccess, setSelectedAccess] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [newStatus, setNewStatus] = useState("true");
  const [userId, setUserId] = useState(null);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newPhoto, setNewPhoto] = useState(null);
  const [newRole, setNewRole] = useState("Customer Care");
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rolemanagement, setRoleManagement] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

  const API_BASE_URL = "http://localhost:5890";

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setRoleManagement(payload.role);
      } catch (err) {
        console.error("Invalid token", err);
      }
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        toast.error("No authentication token found");
        setIsLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      console.log("Fetched users:", response.data);
      setUsers(response.data);
      setIsLoading(false);
    } catch (err) {
      console.error("Error fetching users:", err.response?.data || err.message);
      toast.error("❌ Failed to fetch users: " + (err.response?.data?.error || err.message));
      setIsLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 2 * 1024 * 1024) {
      toast.warn("Image size should be less than 2MB");
      return;
    }
    setNewPhoto(file);
  };

  const resetForm = () => {
    setUserId(null);
    setNewName("");
    setNewEmail("");
    setNewPhone("");
    setNewRole("Customer Care");
    setNewStatus("true");
    setNewPhoto(null);
    setSelectedAccess([]);
    setIsEditing(false);
  };

  const handleAddOrEditUser = async () => {
    if (!newName || !newEmail || !newPhone) {
      toast.warn("⚠️ Please fill in all required fields.");
      return;
    }

    if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(newEmail)) {
      toast.warn("⚠️ Please enter a valid email address.");
      return;
    }

    const formData = new FormData();
    formData.append("fullName", newName);
    formData.append("email", newEmail);
    formData.append("role", newRole);
    formData.append("phone", newPhone);
    formData.append("status", newStatus); // Changed to string "true" or "false"
    formData.append("active", newStatus === "true" ? "true" : "false");
    
    // Send accessPermissions as JSON string
    const accessValues = selectedAccess.map(option => option.value);
    formData.append("accessPermissions", JSON.stringify(accessValues));

    if (newPhoto) {
      formData.append("photo", newPhoto);
    }

    try {
      const token = sessionStorage.getItem("token");
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      };

      if (isEditing) {
        await axios.put(`${API_BASE_URL}/api/users/${userId}`, formData, config);
        toast.success("🎉 User updated successfully!");
      } else {
        await axios.post(`${API_BASE_URL}/api/users`, formData, config);
        toast.success("🎉 User added successfully!");
      }
      
      // Close modal
      const closeBtn = document.getElementById("closeModalBtn");
      if (closeBtn) closeBtn.click();
      
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
      toast.error(error.response?.data?.error || "Operation failed");
    }
  };

  const handleEditUser = (user) => {
    console.log("Editing user:", user);
    setIsEditing(true);
    setUserId(user._id || user.id);
    setNewName(user.fullName || "");
    setNewEmail(user.email || "");
    setNewRole(user.role || "Customer Care");
    setNewPhone(user.phone || "");
    setNewStatus(user.status === "true" || user.status === true ? "true" : "false");
    setNewPhoto(null);

    // Set access permissions
    if (user.accessPermissions && Array.isArray(user.accessPermissions)) {
      const selected = user.accessPermissions
        .map(perm => accessOptions.find(opt => opt.value === perm))
        .filter(Boolean);
      setSelectedAccess(selected);
    } else {
      setSelectedAccess([]);
    }

    // Show modal
    const modalElement = document.getElementById("addUserModal");
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }

    try {
      const token = sessionStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/api/users/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success("🗑️ User deleted successfully");
      fetchUsers();
    } catch (err) {
      console.error("Error deleting user:", err.response?.data || err.message);
      toast.error("Failed to delete user: " + (err.response?.data?.error || err.message));
    }
  };

  // Filter and pagination
  const filteredUsers = users.filter((user) =>
    (user.fullName?.toLowerCase() || "").includes(search.toLowerCase()) ||
    (user.email?.toLowerCase() || "").includes(search.toLowerCase()) ||
    (user.phone || "").includes(search)
  );

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Custom select styles
  const customSelectStyles = {
    control: (base) => ({
      ...base,
      border: "1px solid #dee2e6",
      borderRadius: "0.375rem",
      minHeight: "42px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#86b7fe",
      },
    }),
    multiValue: (base, state) => ({
      ...base,
      backgroundColor: state.data.color ? `${state.data.color}20` : "#e9ecef",
      borderRadius: "4px",
    }),
    multiValueLabel: (base, state) => ({
      ...base,
      color: state.data.color || "#495057",
      fontWeight: 500,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#0d6efd" : state.isFocused ? "#f8f9fa" : "white",
      color: state.isSelected ? "white" : "#212529",
      display: "flex",
      alignItems: "center",
    }),
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return "/default-avatar.png";
    if (photoPath.startsWith("http")) return photoPath;
    if (photoPath.startsWith("/uploads")) return `${API_BASE_URL}${photoPath}`;
    return `${API_BASE_URL}/uploads/${photoPath}`;
  };

  return (
    <div className="container-fluid px-4 py-3">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      
      {/* Stats Cards */}
      <div className="row mb-4 g-3">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100 rounded-3 bg-primary bg-opacity-10">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold small">Total Users</h6>
                  <h3 className="mb-0 fw-bold">{users.length}</h3>
                </div>
                <div className="bg-primary bg-opacity-10 p-3 rounded-circle">
                  <HiOutlineUserGroup size={24} className="text-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100 rounded-3 bg-success bg-opacity-10">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold small">Active Users</h6>
                  <h3 className="mb-0 fw-bold">
                    {users.filter(u => u.status === "true" || u.status === true || u.active === true).length}
                  </h3>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded-circle">
                  <HiCheckCircle size={24} className="text-success" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100 rounded-3 bg-info bg-opacity-10">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold small">Admins</h6>
                  <h3 className="mb-0 fw-bold">{users.filter(u => u.role === "Admin").length}</h3>
                </div>
                <div className="bg-info bg-opacity-10 p-3 rounded-circle">
                  <HiOutlineShieldCheck size={24} className="text-info" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100 rounded-3 bg-warning bg-opacity-10">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold small">Managers</h6>
                  <h3 className="mb-0 fw-bold">{users.filter(u => u.role === "Manager").length}</h3>
                </div>
                <div className="bg-warning bg-opacity-10 p-3 rounded-circle">
                  <HiOutlineChartBar size={24} className="text-warning" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
        {/* Card Header */}
        <div className="card-header bg-white border-0 py-3">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
            <h5 className="mb-2 mb-md-0 fw-semibold d-flex align-items-center gap-2">
              <HiOutlineUserGroup size={20} className="text-primary" />
              User Management
            </h5>
            
            <div className="d-flex flex-column flex-md-row align-items-center gap-3">
              <div className="position-relative" style={{ maxWidth: "400px" }}>
                <HiSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={18} />
                <input
                  type="text"
                  className="form-control ps-5 py-2 rounded-pill shadow-sm border-0"
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ backgroundColor: "#f8f9fa" }}
                />
              </div>
              
              {["Admin", "Manager"].includes(rolemanagement) && (
                <button
                  className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 shadow-sm rounded-pill"
                  data-bs-toggle="modal"
                  data-bs-target="#addUserModal"
                  onClick={resetForm}
                >
                  <HiPlus size={18} />
                  Add User
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-4">User</th>
                  <th>Role</th>
                  <th>Contact</th>
                  <th>Access</th>
                  <th>Status</th>
                  <th className="pe-4 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-5">
                      <PulseLoader color="#0d6efd" size={10} />
                    </td>
                  </tr>
                ) : currentUsers.length > 0 ? (
                  currentUsers.map((user) => (
                    <tr key={user._id || user.id} className="border-top">
                      <td className="ps-4">
                        <div className="d-flex align-items-center gap-3">
                          <div className="position-relative">
                            <img
                              src={getPhotoUrl(user.photo)}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/default-avatar.png";
                              }}
                              alt="avatar"
                              className="rounded-circle border border-2 border-white shadow-sm object-fit-cover"
                              width="42"
                              height="42"
                            />
                            <span className={`position-absolute bottom-0 end-0 translate-middle p-1 bg-${user.status === "true" || user.active ? "success" : "secondary"} rounded-circle border border-2 border-white`} 
                                  style={{ width: "12px", height: "12px" }}></span>
                          </div>
                          <div>
                            <span className="fw-semibold d-block">{user.fullName || "No Name"}</span>
                            <small className="text-muted">
                              ID: {user.userId || (user._id ? user._id.substring(0, 6) : "N/A")}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span 
                          className="badge rounded-pill px-3 py-1"
                          style={{ 
                            backgroundColor: `${roleOptions.find(r => r.value === user.role)?.color || "#6c757d"}20`,
                            color: roleOptions.find(r => r.value === user.role)?.color || "#6c757d"
                          }}
                        >
                          {user.role || "No Role"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="d-block">{user.email || "No email"}</span>
                          <small className="text-muted">{user.phone || 'No phone'}</small>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1" style={{ maxWidth: "200px" }}>
                          {(Array.isArray(user.accessPermissions) ? user.accessPermissions.slice(0, 3) : []).map((perm, idx) => (
                            <span key={idx} className="badge bg-light text-dark rounded-pill px-2 py-1 small">
                              {perm}
                            </span>
                          ))}
                          {Array.isArray(user.accessPermissions) && user.accessPermissions.length > 3 && (
                            <span className="badge bg-light text-dark rounded-pill px-2 py-1 small">
                              +{user.accessPermissions.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge rounded-pill fw-semibold ${user.status === "true" || user.active ? "bg-success-light text-success" : "bg-secondary-light text-secondary"} px-3 py-1`}>
                          <HiCheckCircle size={16} className="me-1" />
                          {user.status === "true" || user.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="pe-4 text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            className={`btn btn-sm btn-icon ${["Admin", "Manager"].includes(rolemanagement) ? "btn-outline-primary" : "btn-outline-secondary disabled"}`}
                            onClick={() => handleEditUser(user)}
                            disabled={!["Admin", "Manager"].includes(rolemanagement)}
                            data-bs-toggle="tooltip"
                            title="Edit user"
                          >
                            <HiPencil size={16} />
                          </button>

                          <button
                            className={`btn btn-sm btn-icon ${rolemanagement === "Admin" ? "btn-outline-danger" : "btn-outline-secondary disabled"}`}
                            onClick={() => handleDeleteUser(user._id || user.id)}
                            disabled={rolemanagement !== "Admin"}
                            data-bs-toggle="tooltip"
                            title="Delete user"
                          >
                            <HiTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-5">
                      <div className="py-4">
                        <img src="/empty-state.svg" alt="No users" width="120" className="mb-3 opacity-75" />
                        <h5 className="fw-semibold">No users found</h5>
                        <p className="text-muted">Try adjusting your search or add a new user</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <div className="card-footer bg-white border-0 py-3">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
              <div className="mb-2 mb-md-0 small text-muted">
                Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} entries
              </div>
              <nav aria-label="Page navigation">
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                      Previous
                    </button>
                  </li>
                  {[...Array(totalPages).keys()].map(number => (
                    <li key={number + 1} className={`page-item ${currentPage === number + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => paginate(number + 1)}>
                        {number + 1}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit User Modal */}
      <div className="modal fade" id="addUserModal" tabIndex="-1" aria-labelledby="addUserModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content shadow-lg border-0 rounded-3">
            <div className="modal-header bg-primary text-white rounded-top-3">
              <h5 className="modal-title" id="addUserModalLabel">
                {isEditing ? (
                  <>
                    <HiPencil className="me-2" />
                    Edit User
                  </>
                ) : (
                  <>
                    <HiPlus className="me-2" />
                    Add New User
                  </>
                )}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
                id="closeModalBtn"
              ></button>
            </div>

            <div className="modal-body px-4 py-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Full Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Enter full name"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Email Address <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="Enter email address"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Role <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={roleOptions}
                    value={roleOptions.find(option => option.value === newRole)}
                    onChange={(selected) => setNewRole(selected.value)}
                    styles={customSelectStyles}
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select role..."
                    formatOptionLabel={(option) => (
                      <div className="d-flex align-items-center">
                        <span 
                          className="rounded-circle me-2" 
                          style={{
                            width: "10px", 
                            height: "10px", 
                            backgroundColor: option.color
                          }}
                        ></span>
                        {option.label}
                      </div>
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Phone Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="Enter phone number"
                    required
                  />
                </div>
                
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Profile Image</label>
                  <div className="d-flex align-items-center gap-3">
                    <div className="flex-grow-1">
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                      <small className="text-muted">Max 2MB (JPEG, PNG)</small>
                    </div>
                    {newPhoto ? (
                      <img
                        src={URL.createObjectURL(newPhoto)}
                        alt="Preview"
                        className="rounded-circle border object-fit-cover"
                        width="60"
                        height="60"
                      />
                    ) : (
                      <div className="rounded-circle border d-flex align-items-center justify-content-center bg-light" 
                           style={{ width: "60px", height: "60px" }}>
                        <span className="text-muted small">No image</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Status</label>
                  <div className="btn-group w-100" role="group">
                    <input
                      type="radio"
                      className="btn-check"
                      name="status"
                      id="statusActive"
                      autoComplete="off"
                      checked={newStatus === "true"}
                      onChange={() => setNewStatus("true")}
                    />
                    <label className={`btn ${newStatus === "true" ? 'btn-success' : 'btn-outline-success'}`} htmlFor="statusActive">
                      Active
                    </label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="status"
                      id="statusInactive"
                      autoComplete="off"
                      checked={newStatus === "false"}
                      onChange={() => setNewStatus("false")}
                    />
                    <label className={`btn ${newStatus === "false" ? 'btn-secondary' : 'btn-outline-secondary'}`} htmlFor="statusInactive">
                      Inactive
                    </label>
                  </div>
                </div>
                
                <div className="col-12">
                  <label className="form-label fw-semibold">Access Permissions</label>
                  <Select
                    isMulti
                    options={accessOptions}
                    value={selectedAccess}
                    onChange={(selected) => setSelectedAccess(selected)}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="Select permissions..."
                    styles={customSelectStyles}
                    formatOptionLabel={(option) => (
                      <div className="d-flex align-items-center">
                        {option.icon}
                        {option.label}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer border-0 rounded-bottom-3 px-4 py-3">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2"
                onClick={handleAddOrEditUser}
              >
                {isEditing ? (
                  <>
                    <HiPencil size={16} />
                    Update User
                  </>
                ) : (
                  <>
                    <HiPlus size={16} />
                    Add User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}